// ProcedureBriefPanel — "Brief me this procedure" modal/side-panel.
//
// Mobile: full-screen modal sheet (absolute overlay).
// Desktop: side panel rendered within the calling layout.
//
// The panel shows:
//   1. Header: procedure name, airport, runway, inbound course, top alt
//   2. Key-fix callouts: IAF/IF/FAF/MAP/DA callouts for IAP;
//      SID: initial fix, top alt, gradient;
//      STAR: entry transition, altitude windows;
//      ODP: initial climb, fix routing
//   3. Leg table: each leg's ident, type, course, altitude, speed, flags
//   4. HILPT / PT warning (bold callout)
//   5. Quiz mode: 3 deterministic questions from CIFP data
//   6. Read-aloud button (Web Speech API)
//   7. "Open plate" button (passed in as prop when available)
//
// DATA CONTRACT: all displayed values come from CIFP. Nothing is invented.

import { useState, useCallback } from 'react';
import { X, Volume2, VolumeX, BookOpen, HelpCircle, ChevronDown, ChevronUp } from '../shell/Icon';
import type { ProcedureBrief, BriefLeg } from '../lib/procedureBrief';
import { generateQuiz, type QuizQuestion } from '../lib/procedureQuiz';
import { useBreakpoint } from '../shell/useBreakpoint';

// ── Types ────────────────────────────────────────────────────────────────

export interface ProcedureBriefPanelProps {
  brief: ProcedureBrief;
  /** When provided, shows an "Open plate" button */
  onOpenPlate?: () => void;
  onClose: () => void;
}

// ── Main Component ───────────────────────────────────────────────────────

export function ProcedureBriefPanel({ brief, onOpenPlate, onClose }: ProcedureBriefPanelProps) {
  const isDesktop = useBreakpoint('lg');
  const [activeTab, setActiveTab] = useState<'brief' | 'quiz'>('brief');
  const [speaking, setSpeaking] = useState(false);
  const [showAllLegs, setShowAllLegs] = useState(false);

  // Web Speech API read-aloud
  const handleReadAloud = useCallback(() => {
    if (!window.speechSynthesis) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const utt = new SpeechSynthesisUtterance(brief.speechText);
    utt.rate = 0.88;
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
    setSpeaking(true);
  }, [brief.speechText, speaking]);

  const content = (
    <div className="flex flex-col h-full overflow-hidden bg-cockpit-900 text-cockpit-100">
      {/* Header */}
      <div
        className="shrink-0 border-b border-cockpit-700 bg-cockpit-800/95 backdrop-blur"
        style={{ paddingTop: isDesktop ? undefined : 'var(--safe-top)' }}
      >
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono bg-cockpit-700 text-cockpit-300 rounded px-1.5 py-0.5">
                {brief.routeType}
              </span>
              <span className="text-xs font-mono text-cockpit-300">{brief.airportIcao}</span>
            </div>
            <div className="text-base font-bold text-cockpit-50 mt-0.5 leading-tight">
              {brief.displayName}
            </div>
            {brief.transitionIdent && (
              <div className="text-xs text-cockpit-400 mt-0.5">
                Transition: <span className="text-cockpit-200 font-mono">{brief.transitionIdent}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onOpenPlate && (
              <button
                onClick={onOpenPlate}
                className="text-[11px] px-2 py-1.5 rounded bg-cockpit-700 hover:bg-cockpit-600 text-cockpit-100 flex items-center gap-1"
                title="Open plate PDF"
              >
                <BookOpen size={12} />
                Plate
              </button>
            )}
            <button
              onClick={handleReadAloud}
              className={[
                'text-[11px] px-2 py-1.5 rounded flex items-center gap-1',
                speaking
                  ? 'bg-ember-600 text-white'
                  : 'bg-cockpit-700 hover:bg-cockpit-600 text-cockpit-100',
              ].join(' ')}
              title={speaking ? 'Stop reading' : 'Read aloud'}
              disabled={!window.speechSynthesis}
            >
              {speaking ? <VolumeX size={12} /> : <Volume2 size={12} />}
              {speaking ? 'Stop' : 'Read'}
            </button>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded text-cockpit-300 hover:text-cockpit-50 hover:bg-cockpit-700"
              aria-label="Close brief"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex border-t border-cockpit-700">
          <button
            onClick={() => setActiveTab('brief')}
            className={[
              'flex-1 py-2 text-xs font-medium transition-colors',
              activeTab === 'brief'
                ? 'text-ember-400 border-b-2 border-ember-500'
                : 'text-cockpit-400 hover:text-cockpit-200 border-b-2 border-transparent',
            ].join(' ')}
          >
            Brief
          </button>
          <button
            onClick={() => setActiveTab('quiz')}
            className={[
              'flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors',
              activeTab === 'quiz'
                ? 'text-ember-400 border-b-2 border-ember-500'
                : 'text-cockpit-400 hover:text-cockpit-200 border-b-2 border-transparent',
            ].join(' ')}
          >
            <HelpCircle size={11} />
            Quiz
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scroll-touch">
        {activeTab === 'brief' ? (
          <BriefTab brief={brief} showAllLegs={showAllLegs} setShowAllLegs={setShowAllLegs} />
        ) : (
          <QuizTab brief={brief} />
        )}
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <div className="w-[26rem] xl:w-[30rem] h-full border-l border-cockpit-700 flex flex-col overflow-hidden">
        {content}
      </div>
    );
  }

  // Mobile: full-screen overlay
  return (
    <div className="absolute inset-0 z-50 flex flex-col">
      {content}
    </div>
  );
}

// ── Brief Tab ────────────────────────────────────────────────────────────

function BriefTab({
  brief, showAllLegs, setShowAllLegs,
}: {
  brief: ProcedureBrief;
  showAllLegs: boolean;
  setShowAllLegs: (v: boolean) => void;
}) {
  return (
    <div className="p-4 space-y-4">
      {/* Summary row */}
      <SummaryCards brief={brief} />

      {/* HILPT / PT warning */}
      {(brief.hasProcedureTurn || brief.hasHilpt) && (
        <div className="rounded-lg border border-amber-500/60 bg-amber-600/10 px-3 py-2">
          <div className="text-[11px] font-bold text-amber-300 uppercase tracking-wide">
            {brief.hasHilpt ? '⚠ Hold-in-Lieu of Procedure Turn (HILPT)' : '⚠ Procedure Turn Required'}
          </div>
          <div className="text-[11px] text-amber-200/80 mt-0.5">
            {brief.hasHilpt
              ? 'Holding pattern is used in lieu of a procedure turn. Brief hold details before descending.'
              : 'A procedure turn is required unless radar vectored to final approach.'}
          </div>
        </div>
      )}

      {/* Key fix callouts by route type */}
      {brief.routeType === 'IAP' && <IapCallouts brief={brief} />}
      {brief.routeType === 'SID' && <SidCallouts brief={brief} />}
      {brief.routeType === 'STAR' && <StarCallouts brief={brief} />}

      {/* Leg table */}
      <section>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-cockpit-400 mb-1.5">
          Legs
        </div>
        <LegTable
          legs={showAllLegs ? brief.legs : brief.legs.slice(0, 10)}
          totalLegs={brief.legs.length}
        />
        {brief.legs.length > 10 && (
          <button
            onClick={() => setShowAllLegs(!showAllLegs)}
            className="mt-2 w-full text-[11px] text-cockpit-400 hover:text-cockpit-200 flex items-center justify-center gap-1"
          >
            {showAllLegs
              ? (<><ChevronUp size={12} /> Show fewer</>)
              : (<><ChevronDown size={12} /> Show all {brief.legs.length} legs</>)}
          </button>
        )}
      </section>

      {/* Mag var note placeholder */}
      <div className="text-[10px] text-cockpit-500 italic">
        Courses are magnetic (°M) as published in CIFP. Verify with current chart.
      </div>
    </div>
  );
}

// ── Summary cards ────────────────────────────────────────────────────────

function SummaryCards({ brief }: { brief: ProcedureBrief }) {
  const cards = [
    brief.inboundCourse !== null && {
      label: brief.routeType === 'SID' ? 'Init Course' : 'Inbound',
      value: `${Math.round(brief.inboundCourse)}°M`,
    },
    brief.topAltitudeStr && {
      label: 'Top Alt',
      value: `${brief.topAltitudeStr} ft`,
    },
    brief.fafAltStr && brief.routeType === 'IAP' && {
      label: 'FAF Alt',
      value: `${brief.fafAltStr} ft`,
    },
    brief.daStr && brief.routeType === 'IAP' && {
      // 2026-05-18: this is the lowest CIFP path altitude between FAF and
      // MAP, NOT the per-minima-line DA/MDA the pilot actually flies.
      // True DA/MDA depends on the minima line (S-ILS DA, S-LPV DA,
      // S-LNAV MDA, CIRCLING) + aircraft category. Label re-scoped to
      // "Path floor" so it doesn't masquerade as the decision altitude.
      label: 'Path floor',
      value: `${brief.daStr} ft`,
    },
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-2">
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg bg-cockpit-800 border border-cockpit-700 px-3 py-2">
          <div className="text-[10px] text-cockpit-400 uppercase tracking-wide">{c.label}</div>
          <div className="text-base font-bold text-cockpit-50 font-mono leading-tight">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── IAP callouts ─────────────────────────────────────────────────────────

function IapCallouts({ brief }: { brief: ProcedureBrief }) {
  return (
    <section>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-cockpit-400 mb-1.5">
        Key Fixes
      </div>
      <div className="space-y-1">
        {brief.iafFixes.length > 0 && (
          <FixRow label="IAF" fixes={brief.iafFixes} color="sky" />
        )}
        {brief.ifFix && <FixRow label="IF" fixes={[brief.ifFix]} color="blue" />}
        {brief.fafFix && (
          <FixRow
            label="FAF"
            fixes={[brief.fafFix]}
            color="amber"
            suffix={brief.fafAltStr ? ` · ${brief.fafAltStr} ft` : undefined}
          />
        )}
        {brief.mapFix && (
          <FixRow
            label="MAP"
            fixes={[brief.mapFix]}
            color="red"
            suffix={brief.daStr ? ` · ${brief.daStr} ft path floor` : undefined}
          />
        )}
        {brief.mahpFix && <FixRow label="MAHP" fixes={[brief.mahpFix]} color="rose" />}
        {!brief.iafFixes.length && !brief.fafFix && !brief.mapFix && (
          <div className="text-[11px] text-cockpit-400 italic">
            Key fixes not identified in CIFP data for this procedure.
          </div>
        )}
      </div>
    </section>
  );
}

// ── SID callouts ─────────────────────────────────────────────────────────

function SidCallouts({ brief }: { brief: ProcedureBrief }) {
  const firstFix = brief.legs.find((l) => l.fixIdent && l.legType !== 'IF')?.fixIdent;
  return (
    <section>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-cockpit-400 mb-1.5">
        Departure Info
      </div>
      <div className="space-y-1">
        {firstFix && <FixRow label="Initial Fix" fixes={[firstFix]} color="sky" />}
        {brief.topAltitudeStr && (
          <KeyValueRow label="Top Altitude" value={`${brief.topAltitudeStr} ft`} />
        )}
        {brief.inboundCourse !== null && (
          <KeyValueRow label="Initial Course/Hdg" value={`${Math.round(brief.inboundCourse)}°M`} />
        )}
        {brief.allTransitions.length > 1 && (
          <KeyValueRow label="Transitions" value={brief.allTransitions.join(', ')} />
        )}
      </div>
    </section>
  );
}

// ── STAR callouts ────────────────────────────────────────────────────────

function StarCallouts({ brief }: { brief: ProcedureBrief }) {
  const altFixes = brief.legs.filter((l) => l.fixIdent && l.altConstraint).slice(0, 4);
  return (
    <section>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-cockpit-400 mb-1.5">
        Arrival Info
      </div>
      <div className="space-y-1">
        {brief.transitionEntryFixes.length > 0 && (
          <FixRow label="Entry Fix" fixes={brief.transitionEntryFixes} color="sky" />
        )}
        {brief.allTransitions.length > 1 && (
          <KeyValueRow label="Transitions" value={brief.allTransitions.join(', ')} />
        )}
        {altFixes.map((leg) => (
          <KeyValueRow key={leg.fixIdent} label={leg.fixIdent} value={`${leg.altConstraint} ft`} />
        ))}
      </div>
    </section>
  );
}

// ── Fix row ───────────────────────────────────────────────────────────────

const FIX_COLORS: Record<string, string> = {
  sky:  'bg-sky-700/30 border-sky-600/50 text-sky-200',
  blue: 'bg-blue-700/30 border-blue-600/50 text-blue-200',
  amber: 'bg-amber-700/30 border-amber-600/50 text-amber-200',
  red:  'bg-red-700/30 border-red-600/50 text-red-200',
  rose: 'bg-rose-700/30 border-rose-600/50 text-rose-200',
};

function FixRow({
  label, fixes, color, suffix,
}: {
  label: string;
  fixes: string[];
  color: keyof typeof FIX_COLORS;
  suffix?: string;
}) {
  const cls = FIX_COLORS[color] ?? FIX_COLORS.sky;
  return (
    <div className={`flex items-center gap-2 rounded px-2.5 py-1.5 border text-xs ${cls}`}>
      <span className="font-bold shrink-0 w-12">{label}</span>
      <span className="font-mono">{fixes.join(', ')}</span>
      {suffix && <span className="ml-1 text-[10px] opacity-75">{suffix}</span>}
    </div>
  );
}

function KeyValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-cockpit-400">{label}</span>
      <span className="font-mono text-cockpit-100">{value}</span>
    </div>
  );
}

// ── Leg table ────────────────────────────────────────────────────────────

function LegTable({ legs, totalLegs }: { legs: BriefLeg[]; totalLegs: number }) {
  if (legs.length === 0) {
    return <div className="text-[11px] text-cockpit-400 italic">No leg data available.</div>;
  }

  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-[11px] font-mono border-collapse min-w-[380px]">
        <thead>
          <tr className="text-[10px] text-cockpit-400 uppercase tracking-wide border-b border-cockpit-700">
            <th className="text-left py-1 pr-2 font-normal">Fix</th>
            <th className="text-left py-1 pr-2 font-normal">Type</th>
            <th className="text-right py-1 pr-2 font-normal">Crs°M</th>
            <th className="text-right py-1 pr-2 font-normal">Alt (ft)</th>
            <th className="text-right py-1 font-normal">Spd</th>
          </tr>
        </thead>
        <tbody>
          {legs.map((leg, i) => (
            <LegRow key={i} leg={leg} index={i} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LegRow({ leg, index }: { leg: BriefLeg; index: number }) {
  const flags: string[] = [];
  if (leg.isIaf) flags.push('IAF');
  if (leg.isIntermediateFix) flags.push('IF');
  if (leg.isFaf) flags.push('FAF');
  if (leg.isMap) flags.push('MAP');
  if (leg.isStepdownFix) flags.push('SDF');
  if (leg.isHoldingPattern) flags.push('HOLD');
  if (leg.isFlyOver) flags.push('FO');

  const rowCls = [
    'border-b border-cockpit-800 transition-colors',
    leg.isMissed
      ? 'text-cockpit-400 bg-rose-900/10'
      : index % 2 === 0
        ? 'text-cockpit-100'
        : 'text-cockpit-200 bg-cockpit-800/30',
  ].join(' ');

  return (
    <tr className={rowCls}>
      <td className="py-1 pr-2">
        <div className="flex items-center gap-1.5">
          <span className={leg.fixIdent ? '' : 'text-cockpit-500'}>
            {leg.fixIdent || '—'}
          </span>
          {flags.map((f) => (
            <span
              key={f}
              className={[
                'text-[8px] px-0.5 rounded font-sans font-bold uppercase',
                f === 'FAF' ? 'bg-amber-600/40 text-amber-300' :
                f === 'MAP' ? 'bg-red-600/40 text-red-300' :
                f === 'IAF' ? 'bg-sky-600/40 text-sky-300' :
                f === 'HOLD' ? 'bg-purple-600/40 text-purple-300' :
                'bg-cockpit-600/60 text-cockpit-300',
              ].join(' ')}
            >
              {f}
            </span>
          ))}
        </div>
      </td>
      <td className="py-1 pr-2 text-cockpit-400">{leg.legType}</td>
      <td className="py-1 pr-2 text-right">
        {leg.courseMag !== null ? Math.round(leg.courseMag) : '—'}
      </td>
      <td className="py-1 pr-2 text-right">
        {leg.altConstraint ? (
          <span className={
            leg.altConstraint.startsWith('≥') ? 'text-emerald-400' :
            leg.altConstraint.startsWith('≤') ? 'text-amber-400' :
            leg.altConstraint.includes('–')   ? 'text-sky-400' :
            'text-cockpit-200'
          }>
            {leg.altConstraint}
          </span>
        ) : '—'}
      </td>
      <td className="py-1 text-right text-cockpit-400">
        {leg.speedKt !== null ? `${leg.speedKt}kt` : '—'}
      </td>
    </tr>
  );
}

// ── Quiz Tab ─────────────────────────────────────────────────────────────

function QuizTab({ brief }: { brief: ProcedureBrief }) {
  const { questions, limited } = generateQuiz(brief);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  if (questions.length === 0) {
    return (
      <div className="p-6 text-center text-cockpit-400 text-sm">
        <HelpCircle className="mx-auto mb-2 opacity-40" size={24} />
        <div>Not enough data in this procedure to generate quiz questions.</div>
        <div className="mt-1 text-xs">Try a procedure with published altitude constraints.</div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5">
      {limited && (
        <div className="text-[11px] text-cockpit-400 italic text-center">
          Limited data — only {questions.length} question{questions.length !== 1 ? 's' : ''} available.
        </div>
      )}

      {questions.map((q, qi) => {
        const picked = answers[qi];
        const isRevealed = revealed[qi];

        // Shuffle choices deterministically using question index as seed
        const shuffled = shuffleChoices(q.choices, qi);
        const correctShuffledIdx = shuffled.indexOf(q.choices[0]);

        return (
          <div key={qi} className="rounded-lg border border-cockpit-700 bg-cockpit-800/60 overflow-hidden">
            <div className="px-3 pt-3 pb-2">
              <div className="text-[10px] text-cockpit-400 mb-1">Question {qi + 1}</div>
              <div className="text-sm text-cockpit-100 leading-snug">{q.question}</div>
            </div>
            <div className="px-3 pb-3 space-y-1.5">
              {shuffled.map((choice, ci) => {
                const isCorrect = ci === correctShuffledIdx;
                const isSelected = picked === ci;

                let choiceCls = 'border border-cockpit-700 bg-cockpit-700/30 text-cockpit-200 hover:bg-cockpit-700/60 cursor-pointer';
                if (isRevealed && isCorrect) {
                  choiceCls = 'border border-emerald-500/60 bg-emerald-700/20 text-emerald-300';
                } else if (isRevealed && isSelected && !isCorrect) {
                  choiceCls = 'border border-red-500/60 bg-red-700/20 text-red-300';
                } else if (!isRevealed && isSelected) {
                  choiceCls = 'border border-cockpit-500 bg-cockpit-600/40 text-cockpit-100';
                }

                return (
                  <button
                    key={ci}
                    onClick={() => {
                      if (!isRevealed) {
                        setAnswers((a) => ({ ...a, [qi]: ci }));
                        setRevealed((r) => ({ ...r, [qi]: true }));
                      }
                    }}
                    disabled={isRevealed}
                    className={`w-full text-left rounded px-3 py-2 text-xs transition-colors ${choiceCls}`}
                  >
                    <span className="font-mono text-cockpit-500 mr-1.5">
                      {['A', 'B', 'C', 'D'][ci]}.
                    </span>
                    {choice}
                    {isRevealed && isCorrect && <span className="ml-1.5 text-emerald-400">✓</span>}
                    {isRevealed && isSelected && !isCorrect && <span className="ml-1.5 text-red-400">✗</span>}
                  </button>
                );
              })}
            </div>
            {isRevealed && (
              <div className="border-t border-cockpit-700 px-3 py-2 bg-cockpit-800/50">
                <div className="text-[10px] text-cockpit-400 italic leading-relaxed">
                  {q.explanation}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Reset */}
      <button
        onClick={() => { setAnswers({}); setRevealed({}); }}
        className="w-full text-[11px] text-cockpit-400 hover:text-cockpit-200 py-1"
      >
        Reset quiz
      </button>
    </div>
  );
}

/** Deterministic shuffle given an index seed. */
function shuffleChoices(choices: string[], seed: number): string[] {
  const arr = [...choices];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = (seed * 31 + i * 17) % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
